import { useCallback, useMemo, useRef } from 'react';
import { useMutation, type ApolloCache, type DefaultContext } from '@apollo/client';
import { useToasterDispatch } from 'common/ui/Toaster';
import type { GetMeQuery } from 'generated/graphql';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { GET_ME, UPDATE_GENERAL_SETTINGS } from 'modules/Apollo/queries';
import {
  type DialogId,
  getDialogVisibilityById,
  getDialogsFromVisibilityById,
  type DialogVisibilityById,
} from './dialogSettingsRegistry';

type UseDialogSettingsSectionProps = {
  context: DefaultContext;
};

type UseDialogSettingsSectionResult = {
  dialogVisibilityById: DialogVisibilityById;
  handleToggleDialogVisibility: (dialogId: DialogId, visible: boolean) => Promise<void>;
};

const toDialogSettings = (dialogVisibilityById: DialogVisibilityById) => {
  const dialogs = getDialogsFromVisibilityById(dialogVisibilityById);

  return {
    __typename: 'DialogSettings' as const,
    dialogs: dialogs.map((dialog) => ({
      __typename: 'DialogSetting' as const,
      ...dialog,
    })),
  };
};

type DialogSettingsData = GetMeQuery['me']['generalSettings']['dialogSettings'];
const UPDATE_DIALOG_SETTINGS_ERROR_MESSAGE =
  'An error occurred while updating your dialog settings. Please try again.';

const updateCachedDialogSettings = (
  cache: ApolloCache<unknown>,
  dialogSettings: DialogSettingsData
) => {
  cache.updateQuery<GetMeQuery>({ query: GET_ME }, (existing) => {
    if (existing?.me == null) {
      return existing;
    }

    return {
      ...existing,
      me: {
        ...existing.me,
        generalSettings: {
          ...existing.me.generalSettings,
          dialogSettings,
        },
      },
    };
  });
};

export const useDialogSettings = ({
  context,
}: UseDialogSettingsSectionProps): UseDialogSettingsSectionResult => {
  const [updateGeneralSettings] = useMutation(UPDATE_GENERAL_SETTINGS, { context });
  const { enqueueToast } = useToasterDispatch();
  const { data } = useCurrentUser({ context });

  const generalSettings = data?.me.generalSettings;
  const dialogs = generalSettings?.dialogSettings?.dialogs;
  const notificationVisibilityMode = generalSettings?.notifications?.visibilityMode ?? 'all';
  const notificationDuration = generalSettings?.notifications?.duration ?? 4000;
  const dialogVisibilityById = useMemo(
    () =>
      getDialogVisibilityById(
        (dialogs ?? []).map((dialog) => ({ id: dialog.id, visible: dialog.visible }))
      ),
    [dialogs]
  );
  const latestDialogVisibilityByIdRef = useRef(dialogVisibilityById);
  const latestNotificationsRef = useRef({
    notificationVisibilityMode,
    notificationDuration,
  });

  // Keep latest values available to a stable callback to avoid stale-closure races.
  latestDialogVisibilityByIdRef.current = dialogVisibilityById;
  latestNotificationsRef.current = {
    notificationVisibilityMode,
    notificationDuration,
  };

  const handleToggleDialogVisibility = useCallback(
    async (dialogId: DialogId, visible: boolean) => {
      if (latestDialogVisibilityByIdRef.current[dialogId] === visible) {
        return;
      }

      const previousDialogVisibilityById = {
        ...latestDialogVisibilityByIdRef.current,
      };
      const nextDialogVisibilityById: DialogVisibilityById = {
        ...previousDialogVisibilityById,
        [dialogId]: visible,
      };
      const { notificationDuration, notificationVisibilityMode } = latestNotificationsRef.current;
      latestDialogVisibilityByIdRef.current = nextDialogVisibilityById;

      const nextDialogs = getDialogsFromVisibilityById(nextDialogVisibilityById);
      const nextDialogSettings = toDialogSettings(nextDialogVisibilityById);

      try {
        await updateGeneralSettings({
          variables: {
            dialogSettings: {
              dialogs: nextDialogs,
            },
          },
          optimisticResponse: {
            __typename: 'Mutation',
            updateGeneralSettings: {
              __typename: 'GeneralSettings',
              notifications: {
                __typename: 'NotificationsSettings',
                visibilityMode: notificationVisibilityMode,
                duration: notificationDuration,
              },
              dialogSettings: nextDialogSettings,
            },
          },
          update: (proxy) => {
            updateCachedDialogSettings(proxy, nextDialogSettings);
          },
        });
      } catch {
        // Apollo removes optimistic cache layers for failed mutations.
        enqueueToast(UPDATE_DIALOG_SETTINGS_ERROR_MESSAGE, { severity: 'error' });
      }
    },
    [enqueueToast, updateGeneralSettings]
  );

  return {
    dialogVisibilityById,
    handleToggleDialogVisibility,
  };
};
